import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
export function useFollow(targetUserId) {
  const [following, setFollowing]   = useState(false);
  const [loading, setLoading]       = useState(true);
  const [toggling, setToggling]     = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    if (!targetUserId) return;
    checkStatus();
  }, [targetUserId]);

  // No follower count here any more: the profile header stopped showing one,
  // and nothing else read it, so the count query went with it.
  //
  // The error is cleared here as well as in toggle(), so a failed follow on
  // one profile can never show under the next. Today PublicProfile swaps in
  // its loading screen between profiles (PublicProfile.jsx, `if (loading)
  // return`), which unmounts this and resets it anyway; this keeps it right
  // if that screen ever goes and the button stays mounted across profiles.
  async function checkStatus() {
    setLoading(true);
    setError('');
    const user = await getCurrentUser();

    const followCheck = user
      ? await supabase.from('follows')
          .select('follower_id')
          .eq('follower_id', user.id)
          .eq('following_id', targetUserId)
          .maybeSingle()
      : { data: null };

    setFollowing(!!followCheck.data);
    setLoading(false);
  }

  // Returns { signedOut: true } when nobody is signed in, so the button can
  // send the visitor to sign up. This used to return quietly, so clicking
  // Follow while signed out did nothing at all.
  //
  // The button only changes once the database has taken the write. It used
  // to flip whether or not the write landed, so a failed follow showed
  // "✓ Following" until the next reload.
  async function toggle() {
    const user = await getCurrentUser();
    if (!user) return { signedOut: true };
    if (user.id === targetUserId) return {}; // can't follow yourself
    setToggling(true);
    setError('');

    // try/finally so a thrown error (network, aborted lock) can't leave the
    // button stuck on "..." -- the client normally returns { error }, but not
    // always.
    try {
      if (following) {
        const { error: delError } = await supabase.from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUserId);
        if (delError) setError("Couldn't unfollow. Try again.");
        else setFollowing(false);
      } else {
        const { error: insError } = await supabase.from('follows')
          .insert({ follower_id: user.id, following_id: targetUserId });
        // 23505 is follows_follower_id_following_id_key: this reader already
        // follows them (from another tab, say), which is the state they asked
        // for, so it is not shown as a failure.
        if (insError && insError.code !== '23505') setError("Couldn't follow. Try again.");
        else setFollowing(true);
      }
    } catch {
      setError(following ? "Couldn't unfollow. Try again." : "Couldn't follow. Try again.");
    } finally {
      setToggling(false);
    }
    return {};
  }

  return { following, loading, toggling, error, toggle };
}
